import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { cn } from '@/lib/utils'
import { Clock } from 'lucide-react'
import { trpc } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { HintLabel, InfoTooltip } from './info-tooltip'

/** Convert UTC hour (0-23) → user's local hour and format as a 1-2pm style window. */
function formatHourWindow(utcHour: number): string {
  // Compose a UTC date at the given hour today, format the local hour with Intl.
  const d = new Date()
  d.setUTCHours(utcHour, 0, 0, 0)
  const localHour = d.getHours()
  const start = localHour
  const end = (localHour + 1) % 24
  const fmt = (h: number) => {
    if (h === 0) return '12am'
    if (h === 12) return '12pm'
    return h < 12 ? `${h}am` : `${h - 12}pm`
  }
  return `${fmt(start)}-${fmt(end)}`
}

export function BestTimeToPostCard({ platforms }: { platforms: PlatformType[] }) {
  const { data, isLoading } = trpc.analytics.bestTimeToPost.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
  })

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Clock size={14} className="text-muted-foreground" />
          Best Time to Post
          <InfoTooltip>
            Hour-of-day windows where your posts pulled the highest average engagement. Times shown
            in your local timezone; we group all published posts in your filter regardless of date.
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <SkeletonRows />}

        {data && !data.hasEnoughData && (
          <p className="text-muted-foreground py-4 text-xs">
            We need at least <span className="font-medium">{data.minPosts}</span> tracked posts to
            spot a pattern — you have <span className="font-medium">{data.totalPosts}</span>.
          </p>
        )}

        {data && data.hasEnoughData && data.topWindows.length > 0 && (
          <div className="space-y-3">
            {data.topWindows.map((w, idx) => {
              const max = data.topWindows[0]!.avgEngagement
              const widthPct = max > 0 ? (w.avgEngagement / max) * 100 : 0
              const isBest = idx === 0
              return (
                <div key={w.startHourUtc}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-foreground font-medium">
                        {formatHourWindow(w.startHourUtc)}
                      </span>
                      {isBest && (
                        <HintLabel
                          hint="Highest average engagement of any hour bucket — start with this window and test variations around it."
                          className="no-underline">
                          <span className="rounded-full bg-purple-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                            Best
                          </span>
                        </HintLabel>
                      )}
                    </div>
                    <span className="text-muted-foreground">{w.postCount} posts</span>
                  </div>
                  <div className="bg-muted relative h-2 overflow-hidden rounded-full">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        isBest ? 'bg-purple-600' : idx === 1 ? 'bg-pink-500' : 'bg-blue-500',
                      )}
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i}>
          <div className="bg-muted mb-1 h-3 w-20 animate-pulse rounded" />
          <div className="bg-muted h-2 animate-pulse rounded-full" />
        </div>
      ))}
    </div>
  )
}
